import React from "react";
import "./Skeleton.css";

/**
 * Base primitive skeleton bone
 */
export const SkeletonBone = ({
  width = "100%",
  height = "16px",
  borderRadius,
  circle = false,
  className = "",
  style = {},
}) => {
  return (
    <span
      className={`custom-skeleton-bone ${circle ? "circle" : ""} ${className}`}
      style={{
        width,
        height,
        borderRadius: borderRadius || (circle ? "50%" : undefined),
        ...style,
      }}
    />
  );
};

export default SkeletonBone;

/**
 * Custom Skeleton for the main Dashboard screen
 */
export const DashboardSkeleton = () => {
  return (
    <div className="skeleton-dashboard-wrapper">
      {/* Top 8 Stat Cards Grid */}
      <div className="skeleton-dash-stats-grid">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="skeleton-stat-card">
            <div style={{ flex: 1 }}>
              <SkeletonBone width="60%" height="13px" style={{ marginBottom: "8px" }} />
              <SkeletonBone width="80%" height="24px" />
            </div>
            <SkeletonBone width="42px" height="42px" circle={true} />
          </div>
        ))}
      </div>

      {/* Middle Row: Main Performance Chart + Donut + Financial Overview */}
      <div className="skeleton-dash-middle-grid">
        {/* Performance Chart Skeleton */}
        <div className="skeleton-card-container">
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
            <SkeletonBone width="140px" height="20px" />
            <SkeletonBone width="100px" height="24px" borderRadius="16px" />
          </div>
          <SkeletonBone width="100%" height="260px" borderRadius="6px" />
        </div>

        {/* Duty Slip Donut Chart Skeleton */}
        <div className="skeleton-card-container">
          <SkeletonBone width="120px" height="20px" style={{ marginBottom: "20px" }} />
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <SkeletonBone width="150px" height="150px" circle={true} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-around", marginTop: "16px" }}>
            <SkeletonBone width="60px" height="14px" />
            <SkeletonBone width="60px" height="14px" />
          </div>
        </div>

        {/* Financial Overview Skeleton */}
        <div className="skeleton-card-container">
          <SkeletonBone width="130px" height="20px" style={{ marginBottom: "16px" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div>
              <SkeletonBone width="50%" height="12px" style={{ marginBottom: "6px" }} />
              <SkeletonBone width="85%" height="22px" />
            </div>
            <div>
              <SkeletonBone width="60%" height="12px" style={{ marginBottom: "6px" }} />
              <SkeletonBone width="75%" height="20px" />
            </div>
            <div>
              <SkeletonBone width="40%" height="12px" style={{ marginBottom: "6px" }} />
              <SkeletonBone width="65%" height="18px" />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row: Recent Bookings & Activities */}
      <div className="skeleton-dash-bottom-grid">
        <div className="skeleton-card-container">
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
            <SkeletonBone width="160px" height="20px" />
            <SkeletonBone width="80px" height="16px" />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                <SkeletonBone width="35%" height="15px" />
                <SkeletonBone width="25%" height="15px" />
                <SkeletonBone width="15%" height="15px" />
              </div>
            ))}
          </div>
        </div>

        <div className="skeleton-card-container">
          <SkeletonBone width="120px" height="20px" style={{ marginBottom: "16px" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <SkeletonBone width="32px" height="32px" circle={true} />
                <div style={{ flex: 1 }}>
                  <SkeletonBone width="70%" height="13px" style={{ marginBottom: "4px" }} />
                  <SkeletonBone width="40%" height="11px" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Custom Skeleton for Master Tables (Ledgers, Users, Vehicles, Services, VehicleTypes, Groups, Taxes, Companies, Selectdata)
 */
export const MasterTableSkeleton = ({
  columns = 6,
  rows = 6,
  hasToolbar = true,
  titleWidth = "180px",
}) => {
  return (
    <div className="skeleton-master-table-wrapper">
      {/* Header bar */}
      <div className="skeleton-table-header-row">
        <SkeletonBone width={titleWidth} height="22px" />
        <SkeletonBone width="90px" height="32px" borderRadius="6px" />
      </div>

      {/* Toolbar / Search Filter */}
      {hasToolbar && (
        <div className="skeleton-table-toolbar">
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <SkeletonBone width="110px" height="34px" borderRadius="6px" />
            <SkeletonBone width="80px" height="28px" borderRadius="4px" />
          </div>
          <SkeletonBone width="240px" height="34px" borderRadius="6px" />
        </div>
      )}

      {/* Table */}
      <div className="skeleton-table-wrapper">
        <table className="skeleton-table-base">
          <thead>
            <tr>
              {Array.from({ length: columns }).map((_, i) => (
                <th key={i}>
                  <SkeletonBone width={i === 0 ? "70%" : i === columns - 1 ? "40px" : "60%"} height="14px" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }).map((_, rIdx) => (
              <tr key={rIdx} className="skeleton-table-row">
                {Array.from({ length: columns }).map((_, cIdx) => (
                  <td key={cIdx}>
                    <SkeletonBone
                      width={
                        cIdx === 0
                          ? "85%"
                          : cIdx === columns - 1
                          ? "50px"
                          : `${45 + ((rIdx + cIdx) % 4) * 15}%`
                      }
                      height="15px"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="skeleton-table-footer">
        <SkeletonBone width="120px" height="14px" />
        <div style={{ display: "flex", gap: "6px" }}>
          <SkeletonBone width="30px" height="28px" borderRadius="4px" />
          <SkeletonBone width="30px" height="28px" borderRadius="4px" />
          <SkeletonBone width="30px" height="28px" borderRadius="4px" />
        </div>
      </div>
    </div>
  );
};

/**
 * Skeleton Rows to inject inside a <tbody> of any existing HTML table
 */
export const TableRowsSkeleton = ({ columns = 5, rows = 5 }) => {
  return (
    <>
      {Array.from({ length: rows }).map((_, rIdx) => (
        <tr key={rIdx} className="skeleton-table-row">
          {Array.from({ length: columns }).map((_, cIdx) => (
            <td key={cIdx} className="skeleton-tr-cell">
              <SkeletonBone
                width={
                  cIdx === columns - 1 && columns > 4
                    ? "60px"
                    : `${50 + ((rIdx * 2 + cIdx * 3) % 4) * 12}%`
                }
                height="15px"
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

/**
 * Custom Skeleton for 2-Column Financial Statements (Balance Sheet & Profit and Loss)
 */
export const FinancialStatementSkeleton = ({ title = "Financial Statement" }) => {
  return (
    <div className="skeleton-financial-wrapper">
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "14px", alignItems: "center" }}>
        <SkeletonBone width="200px" height="24px" />
        <div style={{ display: "flex", gap: "8px" }}>
          <SkeletonBone width="70px" height="30px" borderRadius="6px" />
          <SkeletonBone width="70px" height="30px" borderRadius="6px" />
        </div>
      </div>

      <div className="skeleton-financial-grid">
        {/* Left Column (e.g. Liabilities / Expenses) */}
        <div className="skeleton-financial-col">
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "2px solid #e2e8f0", marginBottom: "8px" }}>
            <SkeletonBone width="140px" height="18px" />
            <SkeletonBone width="80px" height="18px" />
          </div>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skeleton-statement-row">
              <SkeletonBone width={i % 2 === 0 ? "55%" : "40%"} height="14px" />
              <SkeletonBone width="75px" height="14px" />
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "14px", marginTop: "12px", borderTop: "2px solid #cbd5e1" }}>
            <SkeletonBone width="90px" height="18px" />
            <SkeletonBone width="100px" height="18px" />
          </div>
        </div>

        {/* Right Column (e.g. Assets / Income) */}
        <div className="skeleton-financial-col">
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "2px solid #e2e8f0", marginBottom: "8px" }}>
            <SkeletonBone width="140px" height="18px" />
            <SkeletonBone width="80px" height="18px" />
          </div>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skeleton-statement-row">
              <SkeletonBone width={i % 2 === 0 ? "50%" : "45%"} height="14px" />
              <SkeletonBone width="75px" height="14px" />
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "14px", marginTop: "12px", borderTop: "2px solid #cbd5e1" }}>
            <SkeletonBone width="90px" height="18px" />
            <SkeletonBone width="100px" height="18px" />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Custom Skeleton for User Profile screen
 */
export const ProfileSkeleton = () => {
  return (
    <div className="skeleton-profile-container">
      {/* Header Avatar card */}
      <div className="skeleton-profile-header-card">
        <SkeletonBone width="76px" height="76px" circle={true} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
          <SkeletonBone width="200px" height="24px" />
          <SkeletonBone width="140px" height="14px" />
          <div style={{ display: "flex", gap: "10px", marginTop: "4px" }}>
            <SkeletonBone width="90px" height="22px" borderRadius="12px" />
            <SkeletonBone width="110px" height="22px" borderRadius="12px" />
          </div>
        </div>
      </div>

      {/* Profile Form Grid */}
      <div className="skeleton-profile-grid">
        <div className="skeleton-profile-card">
          <SkeletonBone width="150px" height="20px" style={{ marginBottom: "16px" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div>
              <SkeletonBone width="35%" height="12px" style={{ marginBottom: "6px" }} />
              <SkeletonBone width="100%" height="36px" borderRadius="6px" />
            </div>
            <div>
              <SkeletonBone width="30%" height="12px" style={{ marginBottom: "6px" }} />
              <SkeletonBone width="100%" height="36px" borderRadius="6px" />
            </div>
            <div>
              <SkeletonBone width="40%" height="12px" style={{ marginBottom: "6px" }} />
              <SkeletonBone width="100%" height="36px" borderRadius="6px" />
            </div>
          </div>
        </div>

        <div className="skeleton-profile-card">
          <SkeletonBone width="160px" height="20px" style={{ marginBottom: "16px" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div>
              <SkeletonBone width="35%" height="12px" style={{ marginBottom: "6px" }} />
              <SkeletonBone width="100%" height="36px" borderRadius="6px" />
            </div>
            <div>
              <SkeletonBone width="30%" height="12px" style={{ marginBottom: "6px" }} />
              <SkeletonBone width="100%" height="36px" borderRadius="6px" />
            </div>
            <div style={{ marginTop: "10px" }}>
              <SkeletonBone width="120px" height="38px" borderRadius="6px" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Custom Skeleton for Configuration Screen
 */
export const ConfigurationSkeleton = () => {
  return (
    <div className="skeleton-config-wrapper">
      {Array.from({ length: 3 }).map((_, cIdx) => (
        <div key={cIdx} className="skeleton-config-card">
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <SkeletonBone width="36px" height="36px" borderRadius="8px" />
            <div>
              <SkeletonBone width="200px" height="18px" style={{ marginBottom: "4px" }} />
              <SkeletonBone width="280px" height="12px" />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px" }}>
            {Array.from({ length: 3 }).map((_, fIdx) => (
              <div key={fIdx}>
                <SkeletonBone width="50%" height="12px" style={{ marginBottom: "6px" }} />
                <SkeletonBone width="100%" height="36px" borderRadius="6px" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Custom Skeleton for Company Selection screen
 */
export const CompanySelectSkeleton = () => {
  return (
    <div className="skeleton-company-select-card">
      <SkeletonBone width="54px" height="54px" circle={true} style={{ marginBottom: "8px" }} />
      <SkeletonBone width="220px" height="22px" />
      <SkeletonBone width="160px" height="14px" style={{ marginBottom: "12px" }} />
      <SkeletonBone width="100%" height="42px" borderRadius="8px" />
      <SkeletonBone width="100%" height="42px" borderRadius="8px" style={{ marginTop: "8px" }} />
    </div>
  );
};

/**
 * Custom Skeleton for Fares Screen
 */
export const FaresSkeleton = () => {
  return (
    <div className="skeleton-fares-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="skeleton-fare-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <SkeletonBone width="60%" height="18px" />
            <SkeletonBone width="40px" height="20px" borderRadius="10px" />
          </div>
          <SkeletonBone width="45%" height="13px" />
          <div style={{ display: "flex", gap: "6px", margin: "6px 0" }}>
            <SkeletonBone width="50px" height="18px" borderRadius="4px" />
            <SkeletonBone width="65px" height="18px" borderRadius="4px" />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #f1f5f9" }}>
            <SkeletonBone width="70px" height="20px" />
            <div style={{ display: "flex", gap: "6px" }}>
              <SkeletonBone width="28px" height="28px" borderRadius="4px" />
              <SkeletonBone width="28px" height="28px" borderRadius="4px" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Custom Skeleton for Voucher Modify Selector / Voucher Form
 */
export const VoucherModifySkeleton = () => {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
      <SkeletonBone width="28px" height="28px" circle={true} />
      <div style={{ flex: 1, display: "flex", gap: "12px", alignItems: "center" }}>
        <SkeletonBone width="180px" height="16px" />
        <SkeletonBone width="120px" height="14px" />
      </div>
      <SkeletonBone width="70px" height="28px" borderRadius="6px" />
    </div>
  );
};

/**
 * Custom Skeleton for Voucher Breakdown Modal
 */
export const VoucherModalSkeleton = () => {
  return (
    <div style={{ padding: "16px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
        <SkeletonBone width="140px" height="20px" />
        <SkeletonBone width="90px" height="20px" />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
            <div style={{ display: "flex", gap: "10px", alignItems: "center", width: "60%" }}>
              <SkeletonBone width="30px" height="18px" borderRadius="4px" />
              <SkeletonBone width="70%" height="15px" />
            </div>
            <SkeletonBone width="80px" height="15px" />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "16px", paddingTop: "12px", borderTop: "2px solid #e2e8f0" }}>
        <SkeletonBone width="100px" height="18px" />
        <SkeletonBone width="90px" height="18px" />
      </div>
    </div>
  );
};
